import { zodResolver } from '@hookform/resolvers/zod';
import { Box, FormControl, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { getSelectorAttribute, invoicesOverviewPageSelectors } from '@vy/app-selectors';
import { Currency, DEPOSIT_TO_PROVIDERS, PAYMENT_PROVIDERS } from '@vy/types';
import { isNotEmptyString, isNotNullOrUndefined, now, vyDate } from '@vy/utils';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { VyDialog } from '../../../../../components/new-design/feedback/VyDialog';
import { VyAutocompleteSelect } from '../../../../../components/new-design/inputs/VyAutocompleteSelect';
import { useReceivePayment } from '../../../../../hooks/use-receive-payment';
import { usePaymentMethods } from '../../../../../hooks/use-payment-methods';
import { useDepositTo } from '../../../../../hooks/use-deposit-to';
import { runMutation } from '../../../../../utils/vy-mutation';
import { getDepositToProviderNameToDisplay } from './constants/depositToProviderMap';
import { getPaymentProviderNameToDisplay } from './constants/paymentProviderMap';
import { PaymentInfoFormSchema, type PaymentInfoFormType } from './mark-as-paid-form-type';
import { mapPaymentFormValuesToSchema } from './utils/map-payment-form-to-schema';
import type { PaymentMethod, DepositTo } from '@vy/api-definition';
import { isNotEmpty } from '@vy/array';

type SelectOption = {
  label: string;
  value: string;
  id?: string;
};

type MarkAsPaidDialogProps = {
  open: boolean;
  invoiceId: string;
  onClose: () => void;
  customerName: string;
  customerId: string;
  currency: Currency;
};

export const MarkAsPaidDialog: React.FC<MarkAsPaidDialogProps> = ({
  open,
  invoiceId,
  onClose,
  customerName,
  customerId,
  currency,
}) => {
  const recievePaymentMutation = useReceivePayment();
  const { data: paymentMethods } = usePaymentMethods(customerId);
  const { data: depositToData } = useDepositTo(customerId);
  const depositToAccounts = depositToData?.depositToAccounts;
  const defaultDepositToAccountId = depositToData?.defaultDepositToAccountId;
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | undefined>(undefined);

  const form = useForm<PaymentInfoFormType>({
    resolver: zodResolver(PaymentInfoFormSchema),
    defaultValues: {
      date: now(),
    },
  });

  useEffect(() => {
    if (isNotNullOrUndefined(defaultDepositToAccountId) && depositToAccounts) {
      const defaultAccount = depositToAccounts.find((account) => account.id === defaultDepositToAccountId);
      if (isNotNullOrUndefined(defaultAccount)) {
        form.setValue('depositTo', { id: defaultAccount.id, name: defaultAccount.name });
      }
    }
  }, [defaultDepositToAccountId, depositToAccounts]);

  const handleSubmit = form.handleSubmit(async (formData) => {
    const trimmedPaymentMethod = formData.paymentMethod?.trim();

    const paymentPayload = mapPaymentFormValuesToSchema({
      ...formData,
      currency,
      customerId,
      paymentMethod: isNotEmptyString(trimmedPaymentMethod) ? trimmedPaymentMethod : undefined,
      paymentMethodId: selectedPaymentMethodId,
      depositTo: formData.depositTo,
    });

    onClose();
    await runMutation(
      recievePaymentMutation,
      {
        ...paymentPayload,
        invoiceId,
      },
      {
        pending: 'Receiving payment...',
        success: 'Payment received',
        error: 'Failed to receive payment',
      },
    );
  });

  const paymentProviderOptions: SelectOption[] = useMemo(() => {
    if (isNotEmpty(paymentMethods)) {
      return paymentMethods.map((method: PaymentMethod) => ({
        label: method.name,
        value: method.name,
        id: method.id,
      }));
    }

    return createPaymentProviderOptions();
  }, [paymentMethods]);

  const depositToOptions: SelectOption[] = useMemo(() => {
    if (depositToAccounts && depositToAccounts.length > 0) {
      return depositToAccounts.map((account: DepositTo) => ({
        label: account.name,
        value: account.name,
        id: account.id,
      }));
    }

    return createDepositToProviderOptions();
  }, [depositToAccounts]);

  return (
    <VyDialog
      open={open}
      onClose={onClose}
      label={`Receive payment from ${customerName}`}
      ctaText="Save"
      cancelButtonText="Cancel"
      onSubmit={handleSubmit}
      onClick={(event) => event.stopPropagation()}
    >
      <FormControl sx={{ padding: '10px', width: '100%', marginBottom: '10px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            {...getSelectorAttribute(invoicesOverviewPageSelectors.markPaidDialog.transactionId)}
            fullWidth
            label="Transaction ID"
            variant="filled"
            error={isNotNullOrUndefined(form.formState.errors.transactionId)}
            helperText={form.formState.errors.transactionId?.message}
            {...form.register('transactionId')}
          />

          <TextField
            {...getSelectorAttribute(invoicesOverviewPageSelectors.markPaidDialog.amount)}
            fullWidth
            label="Amount"
            type="number"
            variant="filled"
            error={isNotNullOrUndefined(form.formState.errors.amount)}
            helperText={form.formState.errors.amount?.message}
            {...form.register('amount', { valueAsNumber: true })}
            slotProps={{
              htmlInput: { min: 0.01, step: 0.01 }
            }} />

          <Controller
            control={form.control}
            name="date"
            render={({ field }) => (
              <DatePicker
                label="Payment date"
                value={isNotNullOrUndefined(field.value) ? vyDate(field.value) : undefined}
                onChange={(date) => {
                  form.setValue('date', date?.toDate() ?? null);
                }}
              />
            )}
          />

          <TextField
            fullWidth
            label="Note"
            variant="filled"
            error={isNotNullOrUndefined(form.formState.errors.note)}
            helperText={form.formState.errors.note?.message}
            {...form.register('note')}
          />

          <Controller
            name="paymentMethod"
            control={form.control}
            render={({ field }) => (
              <VyAutocompleteSelect
                options={paymentProviderOptions}
                fullWidth
                allowCustomOptions
                onInputChange={(inputValue) => {
                  field.onChange(inputValue);
                  const matchingOption = paymentProviderOptions.find(
                    (opt) => opt.value.toLowerCase() === inputValue.toLowerCase(),
                  );
                  setSelectedPaymentMethodId(matchingOption?.id);
                }}
                onOptionSelect={(option) => {
                  const valueToSet = typeof option === 'string' ? option : option?.value;
                  const idToSet = typeof option === 'string' ? undefined : option?.id;

                  field.onChange(valueToSet);
                  setSelectedPaymentMethodId(idToSet);
                }}
                getOptionLabel={(option) => option.label}
                label="Payment method"
              />
            )}
          />

          <Controller
            name="depositTo"
            control={form.control}
            render={({ field }) => {
              const selectedOption = depositToOptions.find(
                (opt) => opt.id === field.value?.id || opt.value === field.value?.name,
              );

              return (
              <VyAutocompleteSelect
                options={depositToOptions}
                value={selectedOption}
                fullWidth
                allowCustomOptions
                onInputChange={(inputValue) => {
                  const matchingOption = depositToOptions.find(
                    (opt) => opt.value.toLowerCase() === inputValue.toLowerCase(),
                  );
                  if (isNotNullOrUndefined(matchingOption)) {
                    field.onChange({ id: matchingOption?.id, name: matchingOption.value });
                    return;
                  }
                  field.onChange({ id: undefined, name: inputValue });
                }}
                onOptionSelect={(option) => {
                  const valueToSet = typeof option === 'string' ? option : option?.value;
                  const idToSet = typeof option === 'string' ? undefined : option?.id;

                  field.onChange({ id: idToSet, name: valueToSet });
                }}
                getOptionLabel={(option) => option.label}
                label="Deposit to"
              />
              );
            }}
          />
        </Box>
      </FormControl>
    </VyDialog>
  );
};

function createPaymentProviderOptions(): SelectOption[] {
  return Object.values(PAYMENT_PROVIDERS).map((paymentProvider) => ({
    label: getPaymentProviderNameToDisplay(paymentProvider),
    value: paymentProvider,
  }));
}

function createDepositToProviderOptions(): SelectOption[] {
  return Object.values(DEPOSIT_TO_PROVIDERS).map((depositToProvider) => ({
    label: getDepositToProviderNameToDisplay(depositToProvider),
    value: depositToProvider,
  }));
}
